<script lang="ts">
  import { onDestroy } from 'svelte';
  import classNames from 'classnames';
  import scrollToElement from 'scroll-to-element';
  // Utils
  import { useCountdown } from '$lib/utils/countdown-store';

  export let offset = 80;

  const countdownStore = useCountdown();
  const displayCountdownLabel = countdownStore.displayLabel;

  let observer: IntersectionObserver | undefined;
  let anchorLinks: HTMLAnchorElement[];

  onDestroy(() => {
    observer?.disconnect();
    if (anchorLinks) {
      anchorLinks.forEach((anchor) => anchor.removeEventListener('click', onAnchorClick));
    }
  });

  const intersectionCallback = (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
    entries.forEach((entry) => {
      if (!entry.target.id) return;
      const hash = `#${entry.target.id}`;
      if (entry.isIntersecting) {
        updateActiveAnchor(hash);
      }
    });
  };

  const updateActiveAnchor = (hash: string) => {
    anchorLinks.forEach((anchor) => {
      if (anchor.hash === hash) {
        anchor.classList.add('active');
      } else {
        anchor.classList.remove('active');
      }
    });
  };

  const mapAnchorLinks = (node: HTMLElement) => {
    setTimeout(() => {
      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(intersectionCallback, {
          rootMargin: `-${offset}px 0px -${offset}px 0px`,
          threshold: 0,
        });
      }
      anchorLinks = Array.from(node.querySelectorAll<HTMLAnchorElement>(':scope > a'));

      if (anchorLinks.length !== 0) {
        anchorLinks[0].classList.add('active');
      }

      anchorLinks.forEach((anchor) => {
        if (anchor.getAttribute('href').startsWith('#')) {
          anchor.addEventListener('click', onAnchorClick);
          const anchorTarget = <HTMLElement>document.querySelector(anchor.hash);
          if (anchorTarget) {
            observer.observe(anchorTarget);
          }
        }
      });
    });
  };

  const onAnchorClick = (event: MouseEvent) => {
    event.preventDefault();
    anchorLinks.forEach((anchor) => {
      anchor.classList.remove('active');
    });
    const el = event.target;
    if (el instanceof HTMLAnchorElement) {
      el.classList.add('active');
      scrollToElement(el.hash, {
        offset: -offset,
        duration: 750,
      });
    }
  };
</script>

<section
  class={classNames('anchor-tabs', {
    stickToPromoLabel: $displayCountdownLabel,
  })}
>
  <div class="anchor-tabs-list" use:mapAnchorLinks>
    <slot />
  </div>
</section>

<style type="text/scss" lang="scss" scoped>
  .anchor-tabs {
    @apply hidden lg:block;
    @apply sticky z-over-sticky-element top-0;

    &::before {
      content: '';
      @apply md:block;
      @apply md:bg-white;
      @apply md:absolute inset-y-0 left-0;
      @apply md:shadow-fake-bottom-border;

      @screen md {
        width: calc((100vw - var(--page-current-width)) / 2 + var(--page-margin));
        margin-left: calc((100vw - var(--page-current-width)) * -0.5 - var(--page-margin));
      }
    }

    /* &::after {
      content: "";
      @apply md:block;
      @apply md:bg-white;
      @apply md:absolute inset-y-0 right-0;
      @apply md:shadow-fake-bottom-border;

      @screen md {
        width: var(--page-margin);
        margin-right: calc(var(--page-margin) * -1);
      }
    } */
  }

  .anchor-tabs.stickToPromoLabel {
    top: var(--promo-height)
  }

  .anchor-tabs-list {
    @apply flex justify-start;
    @apply bg-white;
    @apply overflow-x-auto;
    @apply shadow-fake-bottom-border;

    &::-webkit-scrollbar {
      @apply hidden;
    }

    /* Avoid sudden interruption of the links with the grid gap */
    @screen md {
      margin-right: calc(var(--page-margin) * -1);
    }

    @screen lg {
      margin-right: calc(var(--page-grid-gap) * -1);
    }
  }

  :global(.anchor-tabs-list > a) {
    @apply relative;
    @apply font-semibold tracking-widest uppercase;
    @apply whitespace-nowrap;
    @apply px-9 py-5;

    &::after {
      content: '';
      @apply absolute left-0 bottom-0;
      @apply h-0.5 bg-current w-full bg-opacity-30;
      @apply transform scale-x-0;
      @apply transition-transform ease-in-out duration-200 origin-right;
    }
  }

  :global(.anchor-tabs-list > a.active:after) {
    @apply transform scale-x-100;
    @apply origin-left;
  }
</style>
